import { useEffect, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';
import {
  hasHeicExtension,
  isHeicMimeType,
} from 'src/services/MediaCompression/MediaCompression.functions';
import { isLeadsalesStorageUrl } from 'src/functions/url.functions';
import {
  downloadAttachment,
  resolveAttachmentFilename,
} from 'src/utils/downloadAttachment';
import { tTypeAttachment } from '../../MessageBubble.type.ts';

// HEIC/HEIF (iPhone photos) can't be decoded by Chrome/Firefox/Edge, so <img>
// fires onError even though the file is intact on our CDN. Detection atoms are
// shared with the send-path transcode detector to avoid drift.
function isHeicAttachment(attachment: tTypeAttachment['attachment']): boolean {
  return (
    isHeicMimeType(attachment.mimeType ?? '') ||
    hasHeicExtension(attachment.filename ?? '') ||
    hasHeicExtension(attachment.url ?? '')
  );
}

/**
 * When an <img> load fails, return the URL to offer as a download ONLY when we
 * can be sure the failure is HEIC-undecodable rather than expiry: the file is
 * HEIC AND lives on our permanent https storage (Meta-CDN URLs are signed and
 * expire, so their load errors keep the "expired" copy and no dead link). This
 * both honors the expiry-vs-unsupported distinction and https-guards the link —
 * a chat-sourced URL rendered into an <a href> must never carry a non-https or
 * javascript: scheme (BUG-712; mirrors SharePostContent/AdReferralCard).
 */
function heicDownloadUrl(
  attachment: tTypeAttachment['attachment'],
): string | null {
  if (!isHeicAttachment(attachment)) return null;
  return isLeadsalesStorageUrl(attachment.url) ? attachment.url : null;
}

export default function ImageContent(props: tTypeAttachment) {
  const { t } = useTranslation('chat/chat');
  const [loadError, setLoadError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  useEffect(() => {
    setLoadError(false);
    setDownloadFailed(false);
  }, [props.attachment.url]);

  async function downloadImage(url: string) {
    if (isDownloading) return;
    setIsDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadAttachment(url, resolveAttachmentFilename(props.attachment));
    } catch {
      setDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  }

  if (loadError) {
    const downloadUrl = heicDownloadUrl(props.attachment);

    return (
      <div className="flex flex-col gap-1">
        <div className="flex gap-2 items-center">
          <PhotoIcon className="w-5 h-5 text-primary" />
          <i>
            {t(
              downloadUrl ? 'attachmentUnsupportedFormat' : 'attachmentExpired',
            )}
          </i>
        </div>
        {downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-busy={isDownloading}
            onClick={(event) => {
              event.preventDefault();
              void downloadImage(downloadUrl);
            }}
            className="text-primary underline text-sm"
          >
            {t('downloadImage')}
          </a>
        )}
        {downloadFailed && <p role="alert">{t('downloadImageFailed')}</p>}
      </div>
    );
  }

  return (
    <img
      src={props.attachment.url ?? ''}
      alt={props.attachment.filename ?? ''}
      className="w-72 max-h-80 mb-1 rounded-md cursor-pointer object-cover"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setLoadError(true)}
    />
  );
}
